// File: frontend/src/components/Admin/LinePaySettingsModal.jsx

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { linePayAPI } from '../../services/api';
import Modal from '../Modal';

export default function LinePaySettingsModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [enabled, setEnabled] = useState(true);
  const [accountName, setAccountName] = useState('Irie Roots English Academy (愛瑞美語)');
  const [accountId, setAccountId] = useState('');
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [instructions, setInstructions] = useState('請使用 LINE App 掃描上方 LINE Pay / 一卡通 MONEY QR Code，轉帳時請備註「學生姓名」，轉帳完成後請告知老師以利對帳！');
  
  // Official Merchant API (Optional)
  const [channelId, setChannelId] = useState('');
  const [channelSecretKey, setChannelSecretKey] = useState('');
  const [hasSecretKey, setHasSecretKey] = useState(false);
  const [isSandbox, setIsSandbox] = useState(true);
  const [showMerchantSection, setShowMerchantSection] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
      setConnectionStatus(null);
    }
  }, [isOpen]);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const res = await linePayAPI.testConnection({
        channelId: channelId || undefined,
        channelSecret: channelSecretKey || undefined,
        isSandbox
      });
      setConnectionStatus({ success: true, message: res.message || 'Connection verified successfully!' });
      toast.success('LINE Pay API connection verified!');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Connection test failed.';
      setConnectionStatus({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingConnection(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await linePayAPI.getSettings();
      setEnabled(data.line_pay_enabled);
      setAccountName(data.line_pay_account_name || 'Irie Roots English Academy (愛瑞美語)');
      setAccountId(data.line_pay_account_id || '');
      setQrImageUrl(data.line_pay_qr_image_url || '');
      setInstructions(data.line_pay_instructions || '');
      setChannelId(data.line_pay_channel_id || '');
      setHasSecretKey(data.has_channel_secret || false);
      setIsSandbox(data.line_pay_is_sandbox);
      if (data.line_pay_channel_id) {
        setShowMerchantSection(true);
      }
    } catch (err) {
      console.error('Failed to load LINE Pay settings:', err);
      toast.error('Could not load LINE Pay settings.');
    } finally {
      setLoading(false);
    }
  };

  // Handle local image file upload for QR code
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, etc.).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setQrImageUrl(reader.result);
      toast.success('QR Code image loaded! Click "Save Settings" to apply.');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await linePayAPI.saveSettings({
        line_pay_enabled: enabled,
        line_pay_account_name: accountName,
        line_pay_account_id: accountId,
        line_pay_qr_image_url: qrImageUrl,
        line_pay_instructions: instructions,
        line_pay_channel_id: channelId,
        line_pay_channel_secret_key: channelSecretKey || undefined,
        line_pay_is_sandbox: isSandbox
      });

      toast.success('LINE Pay settings saved successfully!');
      setChannelSecretKey('');
      fetchSettings();
      onClose();
    } catch (err) {
      console.error('Failed to save LINE Pay settings:', err);
      toast.error('Failed to save LINE Pay settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="LINE Pay Settings (LINE Pay 收款設定)" isOpen={isOpen} onClose={onClose}>
      {loading ? (
        <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">Loading settings...</div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4 text-xs">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div>
              <span className="font-bold text-emerald-900 block">Enable LINE Pay (啟用 LINE Pay 付款)</span>
              <span className="text-[11px] text-emerald-700">Allow parents & students to view LINE Pay QR code and payment info</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          {/* Account Display Name */}
          <div>
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Beneficiary / Studio Name (收款名稱 / 戶名)
            </label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="e.g. Sule / Irie Roots English Academy"
              className="w-full border rounded p-2 text-xs focus:ring-1 focus:ring-emerald-500"
              required
            />
          </div>

          {/* Account ID / Phone */}
          <div>
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">
              LINE ID or Mobile Number for Transfer (收款 LINE ID 或 手機號碼 - 選填)
            </label>
            <input
              type="text"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="e.g. 0912-345-678 or teacher_line_id"
              className="w-full border rounded p-2 text-xs focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          {/* QR Code Upload & Preview */}
          <div className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              LINE Pay / iPASS MONEY 收款碼 (QR Code)
            </label>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
              <div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                  Upload your LINE Pay / 一卡通 MONEY personal or merchant QR code:
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="w-full text-xs text-gray-500 dark:text-gray-400 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                />

                <div className="mt-2">
                  <span className="text-[11px] text-gray-400 dark:text-gray-500 block mb-1">Or paste direct image URL:</span>
                  <input
                    type="url"
                    value={qrImageUrl}
                    onChange={(e) => setQrImageUrl(e.target.value)}
                    placeholder="https://.../linepay_qr.png"
                    className="w-full border rounded p-1.5 text-xs bg-white dark:bg-gray-800"
                  />
                </div>
              </div>

              {/* QR Code Preview */}
              <div className="flex flex-col items-center justify-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 min-h-[140px]">
                {qrImageUrl ? (
                  <div className="text-center">
                    <img
                      src={qrImageUrl}
                      alt="LINE Pay QR Code Preview"
                      className="max-h-32 max-w-full object-contain mx-auto rounded shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setQrImageUrl('')}
                      className="text-[10px] text-red-500 hover:underline mt-1 block mx-auto"
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-gray-400 dark:text-gray-500">
                    <span className="text-3xl block mb-1">📱</span>
                    <span className="text-[11px]">No QR code uploaded yet</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div>
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Payment Instructions for Parents (繳費指引說明)
            </label>
            <textarea
              rows={3}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="w-full border rounded p-2 text-xs focus:ring-1 focus:ring-emerald-500"
              placeholder="Enter transfer instructions..."
            />
          </div>

          {/* Collapsible Official LINE Pay Merchant API */}
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowMerchantSection(!showMerchantSection)}
              className="text-xs font-semibold text-slate-600 hover:text-emerald-600 flex items-center gap-1.5 transition-colors"
            >
              <span>{showMerchantSection ? '▼' : '▶'}</span>
              <span>Advanced: Official LINE Pay Merchant API (企業商家 API 連線 - 選填)</span>
            </button>

            {showMerchantSection && (
              <div className="mt-3 space-y-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-[11px] text-slate-500">
                  If you have an official LINE Pay Business Merchant account, enter your Channel ID and Secret Key for online checkout:
                </p>

                <div>
                  <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Channel ID</label>
                  <input
                    type="text"
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                    placeholder="e.g. 1655000000"
                    className="w-full border rounded p-2 text-xs bg-white dark:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Channel Secret Key {hasSecretKey && <span className="text-emerald-600 font-normal">(Configured ✓)</span>}
                  </label>
                  <input
                    type="password"
                    value={channelSecretKey}
                    onChange={(e) => setChannelSecretKey(e.target.value)}
                    placeholder={hasSecretKey ? 'Leave blank to keep existing secret' : 'Paste Channel Secret Key'}
                    className="w-full border rounded p-2 text-xs bg-white dark:bg-gray-800 font-mono"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isSandbox"
                    checked={isSandbox}
                    onChange={(e) => setIsSandbox(e.target.checked)}
                    className="rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="isSandbox" className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                    Use Sandbox Testing Mode (沙盒測試環境)
                  </label>
                </div>

                <div className="pt-2 flex items-center justify-between border-t border-slate-200">
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testingConnection || (!channelId && !hasSecretKey)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white rounded text-xs font-medium shadow-sm transition-colors cursor-pointer"
                  >
                    <span>⚡</span>
                    <span>{testingConnection ? 'Verifying with LINE Pay...' : 'Test Connection (測試連線)'}</span>
                  </button>

                  {connectionStatus && (
                    <span className={`text-[11px] font-semibold ${connectionStatus.success ? 'text-emerald-700' : 'text-red-600'}`}>
                      {connectionStatus.success ? '✓ Connected' : '✕ Test Failed'}
                    </span>
                  )}
                </div>

                {connectionStatus && (
                  <div className={`p-2.5 rounded text-[11px] ${connectionStatus.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {connectionStatus.message}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded text-xs shadow-sm transition-colors"
            >
              {saving ? 'Saving...' : 'Save Settings (儲存設定)'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
